import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { $userStore } from '@lib/store/userManagement';
import { ValidateUtils } from '@shared/utils';
import { type UserProfileResponse } from '@shared/api/rest/users';
import { BaseInput, Button, ErrorMessage, Modal, PhoneInput } from '@shared/widgets';
import { Constants } from '@shared/constants';

interface IForm {
  userName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  position: string;
  phone: { countryCode: string; phoneNumber: string };
}

interface IEditDetailsModalProps {
  show: boolean;
  onClose: () => void;
  user: UserProfileResponse;
}

export const EditDetailsModal = (props: IEditDetailsModalProps) => {
  const userStore = useUnit($userStore.store);

  const { control, formState, handleSubmit, reset } = useForm<IForm>({
    defaultValues: {
      userName: props.user.userName,
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      emailAddress: props.user.emailAddress,
      position: props.user.position,
      phone: { countryCode: props.user.phoneAreaCode || Constants.COUNTRY_CODE, phoneNumber: props.user.phoneNumber },
    },
  });

  useEffect(() => {
    if (!props.show) return;
    reset({
      userName: props.user.userName,
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      emailAddress: props.user.emailAddress,
      position: props.user.position,
      phone: { countryCode: props.user.phoneAreaCode || Constants.COUNTRY_CODE, phoneNumber: props.user.phoneNumber },
    });
  }, [props.show]);

  const closeHandler = () => {
    props.onClose();
    reset({
      userName: props.user.userName,
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      emailAddress: props.user.emailAddress,
      position: props.user.position,
      phone: { countryCode: props.user.phoneAreaCode || Constants.COUNTRY_CODE, phoneNumber: props.user.phoneNumber },
    });
  };
  const submitHandler = async (data: IForm) => {
    const request = {
      ...props.user,
      userName: data.userName,
      firstName: data.firstName,
      lastName: data.lastName,
      emailAddress: data.emailAddress,
      position: data.position,
      phoneAreaCode: data.phone.countryCode,
      phoneNumber: data.phone.phoneNumber,
    };
    await $userStore.events.updateUser({ request, id: props.user.id });
    props.onClose();
  };

  return (
    <Modal isOpen={props.show} onClose={closeHandler} title='Profile options'>
      <form className='flex flex-col gap-5' onSubmit={handleSubmit(submitHandler)} noValidate>
        <Controller
          name='userName'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder='Username' isReadOnly />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='firstName'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder='First name' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='lastName'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder='Last name' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='emailAddress'
          control={control}
          rules={{
            required: ValidateUtils.requiredErrorMessage,
            pattern: { value: ValidateUtils.emailRegex, message: ValidateUtils.emailErrorMessage },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} type='email' placeholder='E-mail' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='position'
          control={control}
          rules={{ required: ValidateUtils.requiredErrorMessage }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder='Position / Role' />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='phone'
          control={control}
          rules={{
            required: ValidateUtils.requiredErrorMessage,
            validate: (value) => {
              if (!value?.countryCode) return ValidateUtils.requiredErrorMessage;
              if (!value?.phoneNumber) return ValidateUtils.requiredErrorMessage;
              if (!ValidateUtils.validatePhoneNumber(value.phoneNumber)) return ValidateUtils.phoneErrorMessage;
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <PhoneInput enableSearch value={field.value} onChange={field.onChange} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Button className='mt-7' color='primary' fullWidth type='submit' disabled={formState.isSubmitting || userStore.loading}>
          Save changes
        </Button>
      </form>
    </Modal>
  );
};
